import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Separator } from "./components/ui/separator";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, ComposedChart, PieChart, Pie, Cell, Line
} from "recharts";
import { TrendingUp, Users, Wallet, Target, BarChart3, Eye, DollarSign, Briefcase, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Separate axios instance without withCredentials for public pages (works in incognito)
const publicAxios = axios.create({ withCredentials: false });

const formatCurrency = (value) => {
  const abs = Math.abs(value || 0);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1000000) return `${sign}\u20AC${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${sign}\u20AC${(abs / 1000).toFixed(1)}K`;
  return `${sign}\u20AC${abs.toFixed(0)}`;
};

const formatNumber = (value) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value?.toFixed(0) || "0";
};

const CHART_COLORS = ["#0d6e4e", "#10b981", "#6ee7b7", "#a7f3d0", "#059669", "#047857"];

const MetricCard = ({ title, value, subtitle, icon: Icon }) => (
  <Card className="border border-border/60" data-testid={`shared-metric-${title.toLowerCase().replace(/[\s\/]/g, '-')}`}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground/60" />}
      </div>
      <p className="text-2xl font-bold tracking-tight">{value}</p>
      {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
    </CardContent>
  </Card>
);

const InputSection = ({ title, items }) => (
  <Card className="border border-border/60">
    <CardHeader className="pb-3">
      <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{title}</CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
        {items.map(([label, value], i) => (
          <div key={i} className="flex justify-between py-1.5 border-b border-border/30 last:border-0">
            <span className="text-sm text-muted-foreground">{label}</span>
            <span className="text-sm font-medium">{value}</span>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
      <p className="font-medium text-sm mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-xs" style={{ color: entry.color }}>
          {entry.name}: {formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  );
};

const YearDashboard = ({ yearKey, yearLabel, model, yearMetrics }) => {
  if (!yearMetrics) return null;
  const ym = yearMetrics;
  const projections = ym.monthly_projections || [];
  const st = model.sales_team?.[yearKey] || {};
  const mb = model.marketing_budget?.[yearKey] || {};
  const smc = model.sales_marketing_cost?.[yearKey] || {};
  const op = model.operating_costs?.[yearKey] || {};
  const gt = model.growth_targets?.[yearKey] || {};
  const sp = model.sales_pipeline?.[yearKey] || {};

  const salesTeamCost = (ym.sales_capacity?.total_sales_team_cost || 0) / 12;

  const goToMarket = (
    (mb.cost_per_lead || 0) * ((ym.pipeline?.leads_required || 0) / 12) +
    (mb.event_costs || 0) + (mb.agency_costs || 0) + (mb.team_salaries || 0) +
    (smc.sales_salaries || 0) + (smc.head_of_sales_salary || 0) +
    (smc.recruiting_costs || 0) + (smc.crm_tools || 0) + (smc.marketing_team_salaries || 0) +
    salesTeamCost +
    (op.customer_success || 0)
  ) * 12;
  const infrastructure = (op.ai_infrastructure || 0) * 12;
  const product = (op.engineering_team || 0) * 12;
  const operations = ((op.admin_legal || 0) + (op.office_tools || 0)) * 12 +
    (op.custom_items || []).reduce((s, i) => s + (i.amount || 0), 0) * 12;

  const pieData = [
    { name: "Go To Market", value: goToMarket, color: "#10B981" },
    { name: "Infrastructure", value: infrastructure, color: "#F59E0B" },
    { name: "Product", value: product, color: "#3B82F6" },
    { name: "Operations", value: operations, color: "#EF4444" },
  ].filter(d => d.value > 0);
  const pieTotal = pieData.reduce((s, d) => s + d.value, 0);

  const totalRev = projections.reduce((s, p) => s + (p.revenue || p.mrr || 0), 0);
  const totalVC = projections.reduce((s, p) => s + (p.variable_costs || 0), 0);
  const totalFC = projections.reduce((s, p) => s + (p.fixed_costs || 0), 0);
  const totalProfit = totalRev - totalVC - totalFC;

  return (
    <div className="space-y-6" data-testid={`shared-year-${yearKey}`}>
      <h2 className="text-xl font-bold tracking-tight border-b border-border pb-3">{yearLabel}</h2>

      {/* Input Data */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputSection title="Obiettivi di Crescita" items={[
          ["MRR Target", formatCurrency(gt.target_mrr || 0)],
          ["Utenti/Account", gt.target_users_per_account || "-"],
          ["Prezzo/Utente", formatCurrency(gt.target_price_per_user || 0)],
          ["Margine Lordo", `${gt.gross_margin || 0}%`],
        ]} />
        <InputSection title="Pipeline Vendite" items={[
          ["Lead \u2192 Discovery", `${sp.lead_to_discovery || 0}%`],
          ["Discovery \u2192 Demo", `${sp.discovery_to_demo || 0}%`],
          ["Demo \u2192 Cliente", `${sp.demo_to_customer || 0}%`],
          ["Ciclo Vendita", `${sp.sales_cycle_months || 0} mesi`],
        ]} />
        <InputSection title="Team Vendite" items={[
          ["Lead/Rep", st.leads_per_sales_rep || "-"],
          ["Commissione/Utente", formatCurrency(st.commission_per_user || 0)],
          ["Ramp Time", `${st.ramp_time_months || 0} mesi`],
          ["Costo Fisso/Rep", formatCurrency(st.fixed_cost_per_rep || 0)],
        ]} />
        <InputSection title="Budget Marketing" items={[
          ["Costo/Lead", formatCurrency(mb.cost_per_lead || 0)],
          ["Eventi", formatCurrency(mb.event_costs || 0)],
          ["Agenzia", formatCurrency(mb.agency_costs || 0)],
          ["Salari Team", formatCurrency(mb.team_salaries || 0)],
        ]} />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <MetricCard title="MRR" value={formatCurrency(ym.basic?.mrr_input || 0)} subtitle={`Fine anno: ${formatCurrency(ym.basic?.mrr_end || 0)}`} icon={TrendingUp} />
        <MetricCard title="Clienti" value={`${ym.basic?.customers_start || 0} \u2192 ${ym.basic?.customers_end || 0}`} subtitle={`ARPA: ${formatCurrency(ym.basic?.arpa || 0)}`} icon={Users} />
        <MetricCard title="Runway" value={`${ym.cash?.runway_months || 0} mesi`} subtitle={`Burn: ${formatCurrency(ym.cash?.monthly_burn_rate || 0)}/mese`} icon={Wallet} />
        <MetricCard title="CAC" value={formatCurrency(ym.unit_economics?.cac || 0)} subtitle={`Payback: ${(ym.unit_economics?.cac_payback_months || 0).toFixed(1)} mesi`} icon={Target} />
        <MetricCard title="LTV/CAC" value={`${(ym.unit_economics?.ltv_cac_ratio || 0).toFixed(1)}x`} subtitle={`LTV: ${formatCurrency(ym.unit_economics?.ltv || 0)}`} icon={BarChart3} />
      </div>

      {/* P&L Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-4">
          <p className="text-xs font-medium text-muted-foreground uppercase">Entrate Totali</p>
          <p className="text-xl font-bold text-emerald-700 mt-1">{formatCurrency(totalRev)}</p>
        </CardContent></Card>
        <Card className="bg-orange-50 border-orange-200"><CardContent className="p-4">
          <p className="text-xs font-medium text-muted-foreground uppercase">Costi Variabili</p>
          <p className="text-xl font-bold text-orange-600 mt-1">{formatCurrency(totalVC)}</p>
          <p className="text-xs text-muted-foreground mt-1">{totalRev > 0 ? Math.round(totalVC / totalRev * 100) : 0}% delle entrate</p>
        </CardContent></Card>
        <Card className="bg-red-50 border-red-200"><CardContent className="p-4">
          <p className="text-xs font-medium text-muted-foreground uppercase">Costi Fissi</p>
          <p className="text-xl font-bold text-red-600 mt-1">{formatCurrency(totalFC)}</p>
          <p className="text-xs text-muted-foreground mt-1">{totalRev > 0 ? Math.round(totalFC / totalRev * 100) : 0}% delle entrate</p>
        </CardContent></Card>
        <Card className={totalProfit >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}><CardContent className="p-4">
          <p className="text-xs font-medium text-muted-foreground uppercase">Profitto Netto</p>
          <p className={`text-xl font-bold mt-1 ${totalProfit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{formatCurrency(totalProfit)}</p>
          <p className="text-xs text-muted-foreground mt-1">{totalRev > 0 ? Math.round(totalProfit / totalRev * 100) : 0}% delle entrate</p>
        </CardContent></Card>
      </div>

      {/* P&L Chart */}
      <Card className="border border-border/60">
        <CardHeader><CardTitle className="text-lg">Conto Economico - Proiezione 12 Mesi</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={380}>
            <ComposedChart data={projections}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={(v) => `M${v}`} />
              <YAxis tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 11 }} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload) return null;
                  const colorMap = { revenue: "#10B981", variable_costs: "#F59E0B", fixed_costs: "#EF4444", profit: "#064E3B" };
                  const labelMap = { revenue: "Entrate", variable_costs: "Costi Variabili", fixed_costs: "Costi Fissi", profit: "Profitto Netto" };
                  const order = ["revenue", "variable_costs", "fixed_costs", "profit"];
                  const sorted = [...payload].sort((a, b) => order.indexOf(a.dataKey) - order.indexOf(b.dataKey));
                  return (
                    <div style={{ backgroundColor: 'white', border: '1px solid #E4E4E7', borderRadius: '4px', padding: '10px 14px', fontSize: '13px' }}>
                      <p style={{ fontWeight: 600, marginBottom: 6 }}>Mese {label}</p>
                      {sorted.map((entry) => (
                        <div key={entry.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                          <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: colorMap[entry.dataKey] || entry.color, flexShrink: 0 }} />
                          <span style={{ color: '#71717A' }}>{labelMap[entry.dataKey] || entry.name}:</span>
                          <span style={{ fontWeight: 600 }}>{formatCurrency(entry.value)}</span>
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
              <Legend
                formatter={(value) => {
                  const labels = { revenue: "Entrate", variable_costs: "Costi Variabili", fixed_costs: "Costi Fissi", profit: "Profitto Netto" };
                  return labels[value] || value;
                }}
              />
              <Bar dataKey="revenue" name="revenue" fill="#10B981" radius={[3, 3, 0, 0]} />
              <Bar dataKey="variable_costs" name="variable_costs" fill="#F59E0B" radius={[3, 3, 0, 0]} />
              <Bar dataKey="fixed_costs" name="fixed_costs" fill="#EF4444" radius={[3, 3, 0, 0]} />
              <Line type="monotone" dataKey="profit" name="profit" stroke="#064E3B" strokeWidth={3} dot={{ fill: '#064E3B', strokeWidth: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Revenue vs Cash + Customer Growth */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-border/60">
          <CardHeader><CardTitle className="text-lg">Revenue vs Cash</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={projections}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={(v) => `M${v}`} />
                <YAxis tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#10b981" fill="#10b98133" />
                <Area type="monotone" dataKey="cash_remaining" name="Cash" stroke="#0d6e4e" fill="#0d6e4e22" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-border/60">
          <CardHeader><CardTitle className="text-lg">Crescita Clienti</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={projections}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={(v) => `M${v}`} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => [value, "Clienti"]} labelFormatter={(label) => `Mese ${label}`} />
                <Bar dataKey="customers" fill="#10B981" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Use of Funds Pie Chart */}
      {pieData.length > 0 && (
        <Card className="border border-border/60">
          <CardHeader><CardTitle className="text-lg">Use of Funds</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="w-full lg:w-1/2">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={70} outerRadius={110} paddingAngle={3} dataKey="value">
                      {pieData.map((entry, i) => <Cell key={i} fill={entry.color} stroke="white" strokeWidth={2} />)}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.[0]) return null;
                        const d = payload[0].payload;
                        const pct = pieTotal > 0 ? Math.round(d.value / pieTotal * 100) : 0;
                        return (
                          <div style={{ backgroundColor: 'white', border: '1px solid #E4E4E7', borderRadius: '4px', padding: '10px 14px', fontSize: '13px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: d.color, flexShrink: 0 }} />
                              <span style={{ fontWeight: 600 }}>{d.name}</span>
                            </div>
                            <p style={{ marginTop: 4 }}>{formatCurrency(d.value)} ({pct}%)</p>
                          </div>
                        );
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-full lg:w-1/2 space-y-4">
                {pieData.map((d) => {
                  const pct = pieTotal > 0 ? Math.round(d.value / pieTotal * 100) : 0;
                  return (
                    <div key={d.name} className="flex items-center gap-4">
                      <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: d.color }} />
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-medium">{d.name}</span>
                          <span className="text-sm font-bold">{formatCurrency(d.value)}</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: d.color }} />
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{pct}% del totale</p>
                      </div>
                    </div>
                  );
                })}
                <div className="pt-3 border-t border-border">
                  <div className="flex justify-between text-sm font-medium">
                    <span>Totale Costi Fissi</span>
                    <span className="font-bold">{formatCurrency(pieTotal)}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pipeline */}
      <Card className="border border-border/60">
        <CardHeader><CardTitle className="text-lg">Pipeline Richiesta</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground uppercase">Lead</p>
              <p className="text-lg font-bold">{formatNumber(ym.pipeline?.leads_required || 0)}</p>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground uppercase">Discovery</p>
              <p className="text-lg font-bold">{formatNumber(ym.pipeline?.discoveries_required || 0)}</p>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground uppercase">Demo</p>
              <p className="text-lg font-bold">{formatNumber(ym.pipeline?.demos_required || 0)}</p>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground uppercase">Sales Rep</p>
              <p className="text-lg font-bold">{ym.sales_capacity?.sales_reps_needed || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default function SharedModelView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [modelName, setModelName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [verifying, setVerifying] = useState(false);

  const token = window.location.pathname.split("/shared/")[1];

  useEffect(() => {
    const fetchSharedModel = async () => {
      try {
        const response = await publicAxios.get(`${API}/shared/${token}`);
        if (response.data.password_required) {
          setPasswordRequired(true);
          setModelName(response.data.model_name || "Modello Finanziario");
        } else {
          setData(response.data);
        }
      } catch (err) {
        setError(err.response?.status === 404 
          ? "Modello non trovato o condivisione revocata." 
          : "Errore nel caricamento del modello.");
      }
      setLoading(false);
    };
    if (token) fetchSharedModel();
  }, [token]);

  const verifyPassword = async () => {
    if (!password.trim()) {
      setPasswordError("Inserisci la password");
      return;
    }
    setVerifying(true);
    setPasswordError("");
    try {
      const response = await publicAxios.post(`${API}/shared/${token}/verify`, { password });
      setData(response.data);
      setPasswordRequired(false);
    } catch (err) {
      setPasswordError(err.response?.status === 403 
        ? "Password non corretta" 
        : "Errore nella verifica");
    }
    setVerifying(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
          <p className="text-muted-foreground">Caricamento modello...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center space-y-4">
            <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <Eye className="h-6 w-6 text-red-500" />
            </div>
            <h2 className="text-xl font-bold">Link non valido</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (passwordRequired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="shared-password-gate">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 space-y-6">
            <div className="text-center space-y-3">
              <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                <Lock className="h-6 w-6 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold">{modelName}</h2>
              <p className="text-sm text-muted-foreground">
                Questo modello è protetto da password. Inseriscila per continuare.
              </p>
            </div>
            <div className="space-y-3">
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordError(""); }}
                onKeyDown={(e) => e.key === "Enter" && verifyPassword()}
                data-testid="shared-password-input"
              />
              {passwordError && (
                <p className="text-sm text-red-500" data-testid="shared-password-error">{passwordError}</p>
              )}
              <Button 
                className="w-full gap-2" 
                onClick={verifyPassword} 
                disabled={verifying}
                data-testid="shared-password-submit"
              >
                {verifying ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                ) : (
                  <Lock className="h-4 w-4" />
                )}
                Accedi al Modello
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { model, metrics, owner_name } = data;
  const bm = model.business_metrics || {};

  return (
    <div className="min-h-screen bg-background" data-testid="shared-model-view">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border/60">
        <div className="max-w-[1400px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/api/static/logo" alt="Aiclo" className="h-8" />
              <div>
                <h1 className="text-lg font-bold tracking-tight">{model.name || "Modello Finanziario"}</h1>
                <p className="text-xs text-muted-foreground">Condiviso da {owner_name} &middot; Sola lettura</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-full">
              <Eye className="h-3.5 w-3.5" />
              Vista pubblica
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8 space-y-10">
        {/* Business Metrics Summary */}
        <InputSection title="Metriche Business Attuali" items={[
          ["Clienti Attuali", bm.current_customers || 0],
          ["MRR Attuale", formatCurrency(bm.current_mrr || 0)],
          ["Utenti/Cliente", bm.avg_users_per_customer || 0],
          ["Prezzo/Utente", formatCurrency(bm.price_per_user || 0)],
          ["Margine Lordo", `${bm.gross_margin || 0}%`],
          ["Churn Annuale", `${bm.annual_churn_rate || 0}%`],
        ]} />

        {/* Cash & Funding */}
        <InputSection title="Cash & Finanziamenti" items={[
          ["Cash Iniziale", formatCurrency(model.cash_funding?.starting_cash || 0)],
          ["Investimenti", formatCurrency(model.cash_funding?.investment_amount || 0)],
        ]} />

        <Separator className="my-2" />

        {/* Year 1, 2, 3 */}
        {["year1", "year2", "year3"].map((yearKey) => (
          <YearDashboard
            key={yearKey}
            yearKey={yearKey}
            yearLabel={`Anno ${yearKey.replace("year", "")}`}
            model={model}
            yearMetrics={metrics?.[yearKey]}
          />
        ))}

        <Separator className="my-2" />

        {/* Consolidated */}
        {metrics?.consolidated && (
          <div className="space-y-6" data-testid="shared-consolidated">
            <h2 className="text-xl font-bold tracking-tight border-b border-border pb-3">Vista Consolidata - 3 Anni</h2>
            
            {metrics.consolidated.bep && (
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <Target className="h-5 w-5 text-emerald-600" />
                  <div>
                    <p className="font-semibold text-emerald-700">Break-Even Point: Mese {metrics.consolidated.bep.month}</p>
                    <p className="text-xs text-emerald-600">{metrics.consolidated.bep.label || ""}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Consolidated P&L Chart */}
            {metrics.consolidated.monthly_projections?.length > 0 && (() => {
              const cProj = metrics.consolidated.monthly_projections;
              const cTotalRev = cProj.reduce((s, p) => s + (p.revenue || 0), 0);
              const cTotalVC = cProj.reduce((s, p) => s + (p.variable_costs || 0), 0);
              const cTotalFC = cProj.reduce((s, p) => s + (p.fixed_costs || 0), 0);
              const cTotalProfit = cTotalRev - cTotalVC - cTotalFC;
              return (
                <>
                  {/* P&L Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-4">
                      <p className="text-xs font-medium text-muted-foreground uppercase">Entrate Totali (3 Anni)</p>
                      <p className="text-xl font-bold text-emerald-700 mt-1">{formatCurrency(cTotalRev)}</p>
                    </CardContent></Card>
                    <Card className="bg-orange-50 border-orange-200"><CardContent className="p-4">
                      <p className="text-xs font-medium text-muted-foreground uppercase">Costi Variabili (3 Anni)</p>
                      <p className="text-xl font-bold text-orange-600 mt-1">{formatCurrency(cTotalVC)}</p>
                      <p className="text-xs text-muted-foreground mt-1">{cTotalRev > 0 ? Math.round(cTotalVC / cTotalRev * 100) : 0}% delle entrate</p>
                    </CardContent></Card>
                    <Card className="bg-red-50 border-red-200"><CardContent className="p-4">
                      <p className="text-xs font-medium text-muted-foreground uppercase">Costi Fissi (3 Anni)</p>
                      <p className="text-xl font-bold text-red-600 mt-1">{formatCurrency(cTotalFC)}</p>
                      <p className="text-xs text-muted-foreground mt-1">{cTotalRev > 0 ? Math.round(cTotalFC / cTotalRev * 100) : 0}% delle entrate</p>
                    </CardContent></Card>
                    <Card className={cTotalProfit >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}><CardContent className="p-4">
                      <p className="text-xs font-medium text-muted-foreground uppercase">Profitto Netto (3 Anni)</p>
                      <p className={`text-xl font-bold mt-1 ${cTotalProfit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{formatCurrency(cTotalProfit)}</p>
                      <p className="text-xs text-muted-foreground mt-1">{cTotalRev > 0 ? Math.round(cTotalProfit / cTotalRev * 100) : 0}% delle entrate</p>
                    </CardContent></Card>
                  </div>

                  {/* P&L Chart */}
                  <Card className="border border-border/60">
                    <CardHeader><CardTitle className="text-lg">Conto Economico - 36 Mesi</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={380}>
                        <ComposedChart data={cProj}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                          <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={(v) => `M${v}`} interval={2} />
                          <YAxis tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 11 }} />
                          <Tooltip
                            content={({ active, payload, label }) => {
                              if (!active || !payload) return null;
                              const colorMap = { revenue: "#10B981", variable_costs: "#F59E0B", fixed_costs: "#EF4444", profit: "#064E3B" };
                              const labelMap = { revenue: "Entrate", variable_costs: "Costi Variabili", fixed_costs: "Costi Fissi", profit: "Profitto Netto" };
                              const order = ["revenue", "variable_costs", "fixed_costs", "profit"];
                              const sorted = [...payload].sort((a, b) => order.indexOf(a.dataKey) - order.indexOf(b.dataKey));
                              return (
                                <div style={{ backgroundColor: 'white', border: '1px solid #E4E4E7', borderRadius: '4px', padding: '10px 14px', fontSize: '13px' }}>
                                  <p style={{ fontWeight: 600, marginBottom: 6 }}>Mese {label}</p>
                                  {sorted.map((entry) => (
                                    <div key={entry.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                                      <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: colorMap[entry.dataKey] || entry.color, flexShrink: 0 }} />
                                      <span style={{ color: '#71717A' }}>{labelMap[entry.dataKey] || entry.name}:</span>
                                      <span style={{ fontWeight: 600 }}>{formatCurrency(entry.value)}</span>
                                    </div>
                                  ))}
                                </div>
                              );
                            }}
                          />
                          <Legend formatter={(value) => ({ revenue: "Entrate", variable_costs: "Costi Variabili", fixed_costs: "Costi Fissi", profit: "Profitto Netto" }[value] || value)} />
                          <Bar dataKey="revenue" name="revenue" fill="#10B981" radius={[3, 3, 0, 0]} />
                          <Bar dataKey="variable_costs" name="variable_costs" fill="#F59E0B" radius={[3, 3, 0, 0]} />
                          <Bar dataKey="fixed_costs" name="fixed_costs" fill="#EF4444" radius={[3, 3, 0, 0]} />
                          <Line type="monotone" dataKey="profit" name="profit" stroke="#064E3B" strokeWidth={3} dot={false} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </>
              );
            })()}

            {/* Consolidated Revenue vs Cash Chart */}
            {metrics.consolidated.monthly_projections?.length > 0 && (
              <Card className="border border-border/60">
                <CardHeader><CardTitle className="text-lg">Proiezione 36 Mesi</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={metrics.consolidated.monthly_projections}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={(v) => `M${v}`} interval={2} />
                      <YAxis tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#10b981" fill="#10b98133" />
                      <Area type="monotone" dataKey="cash_remaining" name="Cash" stroke="#0d6e4e" fill="#0d6e4e22" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-8 text-xs text-muted-foreground">
          Generato con <span className="font-medium">Aiclo Financial Planner</span>
        </div>
      </main>
    </div>
  );
}